import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Users, SlidersHorizontal, X, Loader2, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { EmptyState } from "@/components/common/Loading";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";
import { useLocalMode } from "@/context/LocalModeContext";
import { DirectoryFilters, DIR_FILTERS } from "@/components/directory/DirectoryFilters";
import { STICKY_RAIL } from "@/components/groups/groupSticky";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { ResultCard } from "@/pages/public/Directory";

// GRP-DEC-309: Role is deliberately NOT a member-discovery filter. Organizers are
// already surfaced separately in the Group's Organizers panel, and an ordinary member has
// no reason to search the roster by administrative role. Roles remain in the membership
// model (authorization + management) and the role BADGE still renders on each card; the
// API's `role` parameter is kept for the Owner/Admin management screens.
const emptyState = () => ({ multi: {}, sort: "role", geoOn: false, center: null,
  radius: 50 });

const SkeletonGrid = () => (
  <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="border-0 p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-[3.75rem] w-[3.75rem] rounded-full" />
          <div className="flex-1 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/3" /></div>
        </div>
        <Skeleton className="mt-4 h-3 w-full" />
      </Card>
    ))}
  </div>
);

/**
 * Group Members tab — a People directory SCOPED to this Group (GRP-DEC-302).
 *
 * Nothing about search or filtering is reimplemented here: the sidebar is the shared
 * `DirectoryFilters` with `kind="people"` (so the filters, labels and reference data are
 * exactly the People directory's — Industry, Skills & interests, the admin-gated
 * Classification, and the shared region/radius control), the cards are the shared
 * `ResultCard` the People directory renders, and paging is `useInfiniteScroll`. Only the
 * genuinely Group-specific control (member Role) is injected through the existing
 * optional `extra` prop.
 *
 * The Group scope is NOT a client concern: `GET /api/groups/{id}/members` authorizes an
 * active member and applies `group_id` + active-membership server-side.
 */
export function GroupMemberDirectory({ groupId, memberCount, onMessage }) {
  const { references } = useReferences();
  const { user } = useAuth();
  const localMode = useLocalMode();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filters, setFilters] = useState(emptyState);
  const people = useMemo(() => DIR_FILTERS.people, []);
  const isAdmin = (user?.role || "") === "admin";

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  // Default the distance centre to the viewer's saved location, like every directory.
  useEffect(() => {
    let ok = true;
    api.get("/directory/my-location").then(({ data }) => {
      if (ok && data && data.lat != null) {
        setFilters((f) => (f.center ? f : { ...f, center: {
          lat: data.lat, lng: data.lng, label: data.label || "My location" } }));
      }
    }).catch(() => {});
    return () => { ok = false; };
  }, []);

  const patch = useCallback((p) => setFilters((f) => ({ ...f, ...p })), []);
  const clearAll = useCallback(() => setFilters((f) => ({
    ...emptyState(), center: f.center })), []);

  useEffect(() => {
    if (!filters.center && filters.sort === "nearest") setFilters((f) => ({ ...f, sort: "role" }));
  }, [filters.center, filters.sort]);

  const multiKey = JSON.stringify(filters.multi);
  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng}` : "";

  const fetchPage = useCallback(async (offset, signal) => {
    const params = { limit: 24, offset };
    if (dq) params.q = dq;
    people.forEach((g) => {
      const arr = filters.multi[g.key];
      if (arr && arr.length) params[g.key] = arr.join(",");
    });
    let sort = filters.sort;
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code;
        else params.country = c.country_code;
      } else {
        params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius;
      }
    } else if (sort === "nearest") {
      sort = "role";
    }
    params.sort = sort;
    if (localMode.localParams) Object.assign(params, localMode.localParams);
    const { data } = await api.get(`/groups/${groupId}/members`, { params, signal });
    return data.members || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, dq, multiKey, filters.sort, filters.geoOn, filters.radius, centerKey,
    localMode.localKey]);

  const resetKey = `gm|${groupId}|${dq}|${multiKey}|${filters.sort}|${filters.geoOn}|${filters.radius}|${centerKey}|${localMode.localKey}`;
  const { items, loadingMore, hasMore, sentinelRef } = useInfiniteScroll({
    fetchPage, resetKey, pageSize: 24 });

  const chips = useMemo(() => {
    const out = [];
    people.forEach((g) => (filters.multi[g.key] || []).forEach((v) =>
      out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (filters.geoOn && filters.center) {
      out.push({ key: "__geo", value: "geo",
        label: isRegionCenter(filters.center) ? `In ${filters.center.label}`
          : `Within ${filters.radius} mi of ${filters.center.label}` });
    }
    return out;
  }, [filters, people, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") return patch({ geoOn: false });
    return patch({ multi: { ...filters.multi,
      [chip.key]: (filters.multi[chip.key] || []).filter((v) => v !== chip.value) } });
  };

  const filterPanel = (
    <DirectoryFilters kind="people" value={filters} onChange={patch} onClear={clearAll}
                      hasActive={chips.length > 0} isAdmin={isAdmin} />
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="group-members-panel">
      <aside className="hidden lg:col-span-3 lg:block">
        {/* Same sticky vocabulary as the Stream rail: the filter rail follows the reader
            and scrolls internally rather than off-screen (correction §3). On phones the
            filters stay in the existing Sheet, which is the better narrow pattern. */}
        <div className={STICKY_RAIL}>{filterPanel}</div>
      </aside>

      <div className="lg:col-span-9">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[200px] flex-1">
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search members by name, headline, location, industry"
                   data-testid="group-members-search" />
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary" className="lg:hidden" data-testid="group-members-mobile-filters">
                <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">{filterPanel}</SheetContent>
          </Sheet>
          <Select value={filters.sort} onValueChange={(v) => patch({ sort: v })}>
            <SelectTrigger className="w-[190px]" data-testid="group-members-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="role">Organizers first</SelectItem>
              <SelectItem value="name">Name (A–Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z–A)</SelectItem>
              <SelectItem value="recent">Newest members</SelectItem>
              <SelectItem value="nearest" disabled={!filters.center}>Nearest</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {chips.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {chips.map((c, i) => (
              <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary"
                      data-testid={`applied-chip-${c.value}`}>
                {c.label}<X className="h-3 w-3" />
              </button>
            ))}
            <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline"
                    data-testid="group-members-filters-clear">Clear all</button>
          </div>
        )}

        {items !== null && (
          <div className="mt-4 text-sm text-muted-foreground" data-testid="group-members-result-count">
            {items.length} of {memberCount} member{memberCount === 1 ? "" : "s"}
          </div>
        )}

        {items === null ? <SkeletonGrid /> : items.length === 0 ? (
          <div className="mt-4">
            <EmptyState icon={Users} title="No members match"
                        description="Try removing a filter or widening your distance."
                        action={<button onClick={clearAll} className="text-primary hover:underline">Clear all filters</button>}
                        testId="group-members-empty" />
          </div>
        ) : (
          <>
            <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3"
                 data-testid="group-members-list">
              {items.map((it) => (
                <div key={it.user_id} className="relative" data-testid={`group-member-${it.user_id}`}>
                  <ResultCard it={it} kind="people" references={references} />
                  {it.role && it.role !== "member" ? (
                    <Badge variant="outline"
                           className="absolute right-3 top-3 z-10 bg-card capitalize"
                           data-testid={`group-member-role-${it.user_id}`}>{it.role}</Badge>
                  ) : null}
                  {/* Group Member Messaging V1 (spec §16) — a Message action on every
                      eligible member. Friendship/follow is irrelevant: Group membership
                      is the relationship that permits it, and the server resolves or
                      creates the ONE canonical 1:1 thread for this Group.
                      Platform User Blocking V1 (§17): a blocked member stays listed, but
                      the action is withheld on the server's direction-free
                      `interaction_available` flag — same neutral state in both
                      directions, and no second block lookup in the client. */}
                  {onMessage && it.user_id !== user?.user_id
                    && it.interaction_available !== false ? (
                    <Button size="sm" variant="secondary"
                            className="absolute bottom-3 right-3 z-10"
                            onClick={() => onMessage(it)}
                            data-testid={`group-member-message-${it.user_id}`}>
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" />Message
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
            <div ref={sentinelRef} aria-hidden="true" className="h-1" />
            {loadingMore && (
              <div className="mt-6 flex justify-center" data-testid="group-members-loading-more">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!hasMore && items.length > 0 && (
              <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="group-members-end">
                You&apos;ve reached the end · {items.length} member{items.length !== 1 ? "s" : ""}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
